// Screen 9 · Library — standards, catalog and the Engineering queue.
// Answers: what does the shop know how to build, with what, in how many minutes, and which
// of it has been checked? One editor pattern for every table: list on the left, detail on
// the right. Nothing is seeded in code; every row here is data.

import { LibrarySuggestions } from './job/Suggestions';
import { useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCrumb } from '../frame/Frame';
import { deleteRow, findRow, insertRow, refresh, rpc, toast, updateRow, useRows, type Row } from '../lib/api';
import { longDate, plural, qty, today } from '../lib/format';
import { currentVersion } from '../lib/library';
import { useSession } from '../lib/session';
import { Btn, Edit, Empty, Field, Head, Info, Ln, Loading, Panel, Seg, Tag } from '../ui/kit';
import { Grip, Move, nextSort, useDragRows } from '../ui/move';
import { SettingRows } from '../ui/settingvalue';
import { cap, useInventoryNames } from './inventory/names';
import { CheckSheet } from './CheckSheet';
import { OfferedAs, StandardChoices, StandardDefault, StandardFilter } from './LibraryEditors';
import { ChecklistLines, SopList } from './Refs';
import { MaterialUses, looksPerPack, packWords, priceWords } from '../ui/pack';

type Kind = 'text' | 'number' | 'bool' | 'select' | 'ref' | 'json' | 'date' | 'long' | 'readonly' | 'custom';
interface Col {
  key: string;
  label: string;
  /** The label is one of the inventory names in Settings, so it follows a rename there. */
  named?: 'safety_stock' | 'order_quantity';
  kind?: Kind;
  options?: string[];
  ref?: string;
  refLabel?: string;
  list?: boolean;
  info?: string;
  /** A column drawn by its own editor (a standard's choices and the materials they stand for). */
  render?: (row: Row, disabled: boolean) => ReactNode;
  /** When the field is shown on a row's panel at all; left out, always. A field holding a value is always shown, so nothing hides a value. */
  show?: (row: Row) => boolean;
}

/** A material that is sheet stock: its category is Sheet Goods or Veneer (Spencer, 2026-10-24: the four sheet fields show only on these, or where one holds a value). */
const isSheet = (row: Row) => /^(sheet goods|veneer)$/i.test(String(row.category ?? '').trim());
type Look = (table: string, id: string) => string;
interface TableDef {
  table: string;
  label: string;
  one: string;
  title: (r: Row) => string;
  sub?: (r: Row, look: Look) => string;
  cols: Col[];
  order: string[];
  library: boolean;
  blank: Row;
  /** Ways the list can be rolled up (Materials: by category then subcategory, or by vendor then category). Each level names a group. */
  groupings?: { key: string; label: string; levels: ((r: Row, look: Look) => string)[] }[];
  /** Dropdowns beside the filter box that keep one value only (Category, Vendor). A row may answer with several values (the keys a material is offered under). */
  filters?: { key: string; label: string; of: (r: Row, look: Look) => string | string[] }[];
  /** The list pane takes more of the width (Materials: most names on one line). */
  wide?: boolean;
  /** Last word on a duplicate's columns, after the generic copy. */
  copy?: (copy: Row, from: Row) => Row;
}

export const TABLES: TableDef[] = [
  {
    table: 'operation', label: 'Operations', one: 'operation', library: true, order: ['sort'], blank: { name: 'New operation' },
    title: (r) => r.name,
    sub: (r, look) => [r.admin004_id ? `#${r.admin004_id}` : '', r.category, look('labor_class', r.labor_class_id)].filter(Boolean).join(' · '),
    cols: [
      { key: 'name', label: 'Name' },
      { key: 'admin004_id', label: 'Admin004 ID' },
      { key: 'category', label: 'Category', kind: 'custom', info: 'One of the time categories (Library → Time categories), so an operation and the time logged against it read the same way. An old word that is not a time category stays until picked again, and says so.', render: (row, disabled) => <CategoryPick row={row} disabled={disabled} /> },
      { key: 'labor_class_id', label: 'Labor class', kind: 'ref', ref: 'labor_class', info: 'Drives cost: minutes × this class\'s rate.' },
      { key: 'phase_id', label: 'Phase', kind: 'ref', ref: 'phase', info: 'What the card generator groups by.' },
      { key: 'work_center_id', label: 'Work center', kind: 'ref', ref: 'work_center', info: 'Where it happens. Drives shop planning, not cost.' },
      { key: 'unit', label: 'Unit' },
      { key: 'setup_minutes', label: 'Setup minutes', kind: 'number', info: 'Paid once per job, or once per batch of the job\'s total when a batch size is set. A route step may be set to carry none.' },
      { key: 'batch_size', label: 'Standard batch size', kind: 'number', info: 'How many units one setup covers. Blank: the setup is paid once per job. 10 sheets: a job of 3.5 sheets rounds up to 4 and pays one setup; 12 sheets pay two.' },
      { key: 'needs_cut_sheet', label: 'Needs a cut sheet', kind: 'bool', info: 'A card with this operation shows "waiting on cut sheet" until a cut sheet file is attached to it. It is a flag: the card can still be pulled.' },
      { key: 'notes', label: 'Notes', kind: 'long' },
      { key: 'retired', label: 'Retired', kind: 'bool' },
    ],
  },
  {
    table: 'material', label: 'Materials', one: 'material', library: true, order: ['sort'], blank: { description: 'New material' },
    title: (r) => r.description,
    // the list is the name and the Admin005 ID only; price and vendor are on the detail (Spencer's change log, 2026-10-07)
    sub: (r) => (r.admin005_id ? `#${r.admin005_id}` : ''),
    wide: true,
    groupings: [
      { key: 'category', label: 'By category', levels: [(r) => (r.category as string) ?? '', (r) => (r.subcategory as string) ?? ''] },
      { key: 'vendor', label: 'By vendor', levels: [(r, look) => look('vendor', r.vendor_id) || 'No vendor', (r) => (r.category as string) ?? ''] },
    ],
    cols: [
      { key: 'description', label: 'Description' },
      { key: 'admin005_id', label: 'Admin005 ID' },
      { key: 'part_number', label: 'Part number' },
      { key: 'price', label: 'Price', kind: 'number', info: 'What the vendor charges, as on the receipt: for one piece, or for the pack when a pack size is set.' },
      { key: 'unit', label: 'Unit' },
      { key: 'pack_size', label: 'Pack size', kind: 'number', info: 'How many of what the recipe lines count the price buys: 10 for pulls sold in packs of 10. One piece then costs the price over the pack size. Blank: the price is for one. Type 1 when the price is already for what the lines count (a reel, when the lines count reels), so the material leaves the "looks priced per pack" list. Changing it returns the material to unverified (Spencer, 2026-10-17).' },
      { key: 'whole_packs', label: 'A job pays for whole packs', kind: 'bool', info: "On (a pull, rarely used twice): the job's pieces round up to whole packs once for the job, so 11 pulls in packs of 10 charge 2 packs. Off (a hinge used on every job, the rest go to stock): only the pieces the job uses, 11 hinges at one piece's price." },
      { key: 'pack_each', label: 'One piece', kind: 'custom', render: (row) => <small>{packWords(row) || 'The price is for one.'}</small> },
      { key: 'used_on', label: 'Used on', kind: 'custom', info: 'The recipe lines that buy it, and the lines that read it through a standard that picks it (Hardware A on a job): what they count is what the pack size is in.', render: (row) => <MaterialUses m={row} /> },
      { key: 'price_updated', label: 'Price updated', kind: 'date' },
      { key: 'category', label: 'Category' },
      { key: 'subcategory', label: 'Subcategory' },
      { key: 'vendor_id', label: 'Vendor', kind: 'ref', ref: 'vendor' },
      { key: 'alt_vendor_id', label: 'Alternate vendor', kind: 'ref', ref: 'vendor' },
      { key: 'lead_time_days', label: 'Lead time, days (this material)', kind: 'number', info: 'Only when it differs from the vendor\'s default. It applies to the primary vendor.' },
      { key: 'vendor_url', label: 'Link on the vendor\'s site' },
      { key: 'nominal_thickness_in', label: 'Sheet thickness, nominal (in)', kind: 'number', show: isSheet, info: 'For a sheet: what it is sold as, as a decimal (0.75). A box formula reads the real thickness as thickness_<role>: this less the undersize.' },
      { key: 'undersize_in', label: 'Sheet undersize (in)', kind: 'number', show: isSheet, info: 'How far under the nominal the sheet really is, as a decimal (0.03125 is 1/32). Blank means not measured yet: the engine then uses the nominal and says so on every box.' },
      { key: 'sheet_length_in', label: 'Sheet length (in)', kind: 'number', show: isSheet, info: 'For a sheet: 96 for a 4 x 8, 120 for a 4 x 10. A box formula reads length × width as sheet_area_<role>; the sheet size is not a standard, it depends on the plywood chosen (Spencer, 2026-10-06).' },
      { key: 'sheet_width_in', label: 'Sheet width (in)', kind: 'number', show: isSheet, info: 'For a sheet: 48 for a 4 x 8.' },
      { key: 'stock_on_hand', label: 'Stock on hand', kind: 'readonly', info: 'A count is corrected from the item in Inventory → Stock, where the reason is recorded.' },
      { key: 'safety_stock', label: 'Safety stock', named: 'safety_stock', kind: 'number', info: 'Never let stock on hand plus what is already on order fall below this. Blank means not tracked.' },
      { key: 'order_quantity', label: 'Order quantity', named: 'order_quantity', kind: 'number', info: 'What one reorder brings in. Blank means order the shortfall.' },
      { key: 'tracking_mode', label: 'Tracking', kind: 'select', options: ['exact', 'bin'], info: 'Exact counts units. Bin is for consumables nobody counts: scan the bin when it runs low.' },
      { key: 'offered_as', label: 'Offered as', kind: 'custom', info: 'The keys this material is offered under: exterior sheet, edge banding. A picker filtered by a key offers only the materials carrying it (a standard\'s role, a material input on a catalog item). Beside the category and subcategory, which stay.', render: (row, disabled) => <OfferedAs row={row} disabled={disabled} /> },
      { key: 'notes', label: 'Notes', kind: 'long' },
    ],
    filters: [
      { key: 'category', label: 'Category', of: (r) => (r.category as string) ?? '' },
      { key: 'vendor', label: 'Vendor', of: (r, look) => look('vendor', r.vendor_id) },
      { key: 'offered', label: 'Offered as', of: (r) => (Array.isArray(r.offered_as) ? (r.offered_as as string[]) : []) },
      // the materials whose unit or name reads as a pack and have no pack size yet, for Spencer to settle one by one
      { key: 'packs', label: 'Packs', of: (r) => (looksPerPack(r) ? 'Looks priced per pack' : r.pack_size !== null && r.pack_size !== undefined && r.pack_size !== '' ? 'Sold in packs' : '') },
    ],
  },
  {
    table: 'vendor', label: 'Vendors', one: 'vendor', library: true, order: ['name'], blank: { name: 'New vendor' },
    title: (r) => r.name,
    sub: (r) => [...(r.does_sub_work ? ['sub'] : []), r.order_method === 'web' ? 'web order' : r.order_method === 'document' ? 'PO document' : 'order method not set', r.lead_time_days !== null ? `${r.lead_time_days} d lead` : 'no lead time'].join(' · '),
    cols: [
      { key: 'name', label: 'Name' },
      { key: 'does_sub_work', label: 'Does sub work', kind: 'bool', info: 'A sub: CustomOne, a countertop shop. Listed first where a sub line on a job picks its vendor (2026-10-25).' },
      { key: 'order_method', label: 'How an order reaches them', kind: 'select', options: ['', 'web', 'document'], info: 'Web: the PO number is keyed into their site\'s cart. Document: ZeroGap makes the PO document to send.' },
      { key: 'lead_time_days', label: 'Lead time, days', kind: 'number', info: 'The default for everything bought from them. A material can override it. It flows to the schedule.' },
      { key: 'url', label: 'Website' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'shipping_rule', label: 'Shipping, for estimates', kind: 'select', options: ['', 'flat', 'free_over', 'percent'], info: 'Used only until the real shipping is on the order. Flat: the amount per order. Free over: the amount, unless the order reaches the threshold. Percent: of the order value.' },
      { key: 'shipping_amount', label: 'Shipping amount $', kind: 'number' },
      { key: 'shipping_threshold', label: 'Free over $', kind: 'number' },
      { key: 'shipping_percent', label: 'Shipping %', kind: 'number' },
      { key: 'note', label: 'Note', kind: 'long' },
    ],
  },
  {
    table: 'work_center', label: 'Work centers', one: 'work center', library: true, order: ['kind', 'name'], blank: { name: 'New station', kind: 'station' },
    title: (r) => r.name,
    sub: (r) => `${r.kind === 'catch_all' ? 'catch-all' : 'station'} · holds ${r.capacity}`,
    cols: [
      { key: 'name', label: 'Name' },
      { key: 'kind', label: 'Kind', kind: 'select', options: ['station', 'catch_all'] },
      { key: 'capacity', label: 'Capacity (people at once)', kind: 'number' },
    ],
  },
  {
    table: 'phase', label: 'Phases', one: 'phase', library: true, order: ['sort'], blank: { name: 'New phase' },
    title: (r) => r.name,
    cols: [{ key: 'name', label: 'Name' }],
  },
  {
    table: 'standard_def', label: 'Standards', one: 'standard', library: true, order: ['sort'], blank: { key: 'new_standard', label: 'New standard' },
    title: (r) => r.label,
    sub: (r) => `${r.kind} · ${String(r.data_type).replace(/_/g, ' ')} · default ${r.data_type === 'material' || r.data_type === 'catalog_item' ? '(picked)' : r.default_value ?? '—'}`,
    // in order (the order Job setup shows them; drag to change it), or rolled up by kind or by type (Spencer's change log, 2026-10-07)
    groupings: [
      { key: 'order', label: 'In order', levels: [] },
      { key: 'kind', label: 'By kind', levels: [(r) => (r.kind as string) ?? '', (r) => String(r.data_type).replace(/_/g, ' ')] },
      { key: 'type', label: 'By type', levels: [(r) => String(r.data_type).replace(/_/g, ' '), (r) => (r.kind as string) ?? ''] },
    ],
    filters: [
      { key: 'kind', label: 'Kind', of: (r) => (r.kind as string) ?? '' },
      { key: 'type', label: 'Type', of: (r) => String(r.data_type).replace(/_/g, ' ') },
    ],
    cols: [
      { key: 'label', label: 'Label' },
      { key: 'key', label: 'Key (the name formulas use)' },
      { key: 'kind', label: 'Kind', kind: 'select', options: ['company', 'job', 'catalog'], info: 'Company standards carry a default a job may change. Job standards are chosen per job. A catalog standard is a pre-staged input (side type, drawer box height): a catalog item pulls it in with "+ Standard input", its type, choices and default come from here, it is typed per item on the job, and it is never on Job setup.' },
      { key: 'data_type', label: 'Type', kind: 'select', options: ['number', 'text', 'boolean', 'choice', 'material', 'catalog_item', 'recipe'], info: 'A "material" or "catalog item" standard holds one row from the library: the job\'s hinge, the job\'s door type. A "recipe" standard holds a recipe name (Shop-built, Cabinotch: any name the catalog items give their recipes): every catalog item picked by it resolves through its recipe of that name.' },
      { key: 'default_value', label: 'Company default', kind: 'custom', render: (row, disabled) => <StandardDefault row={row} disabled={disabled} /> },
      { key: 'unit', label: 'Unit (a number standard)', info: 'How a number standard is shown and typed: in, $, %, sq in. Blank means no unit.' },
      { key: 'allow_item_override', label: 'An item may hold its own value', kind: 'bool', info: 'On: a job item can tick the box beside this standard and hold a different value for itself. Off (hinge placement): every item follows the job; only Job setup changes it.' },
      { key: 'material_filter', label: 'What the picker offers', kind: 'custom', info: 'For a material standard: only materials carrying a key, or in one category and subcategory (Hardware A: Pulls & Knobs). For a catalog-item standard: only items of one family (Door type A: Doors/Panels). Blank offers everything.', render: (row, disabled) => <StandardFilter row={row} disabled={disabled} /> },
      { key: 'choices', label: 'Choices, and the materials each stands for', kind: 'custom', info: 'For a choice standard. The choices in order, each with the rule that makes it unavailable on a job; the material roles the standard fills, each with what its pickers offer; and a material for every choice and role. A job picks the choice once, and every line that reads a role takes its material.', render: (row, disabled) => <StandardChoices row={row} disabled={disabled} /> },
      { key: 'info', label: 'Text behind the i dot', kind: 'long' },
    ],
  },
  {
    table: 'job_type', label: 'Job types', one: 'job type', library: true, order: ['name'], blank: { name: 'New job type' },
    title: (r) => r.name,
    sub: (r) => r.note ?? '',
    cols: [{ key: 'name', label: 'Name' }, { key: 'note', label: 'Note', kind: 'long' }],
  },
  {
    table: 'shop_time_category', label: 'Time categories', one: 'category', library: true, order: ['sort'], blank: { name: 'New category' },
    title: (r) => r.name,
    sub: (r, look) => look('time_category_list', r.list_id) || 'in no list',
    cols: [
      { key: 'name', label: 'Name' },
      { key: 'list_id', label: 'List', kind: 'ref', ref: 'time_category_list', info: 'A person logging time picks from the list they are given. The shop\'s list and the owner\'s list are separate.' },
    ],
  },
  {
    table: 'time_category_list', label: 'Time category lists', one: 'list', library: false, order: ['sort'], blank: { name: 'New list', key: `list_${Date.now() % 100000}` },
    title: (r) => r.name,
    cols: [{ key: 'name', label: 'Name' }, { key: 'key', label: 'Key', kind: 'readonly' }],
  },
];

const TABS = [
  { key: 'home', label: 'Needs your check' },
  { key: 'catalog', label: 'Catalog items' },
  { key: 'families', label: 'Catalog families' },
  { key: 'operation', label: 'Operations' },
  { key: 'material', label: 'Materials' },
  { key: 'vendor', label: 'Vendors' },
  { key: 'rates', label: 'Labor rates' },
  { key: 'standard_def', label: 'Standards' },
  { key: 'job_type', label: 'Job types' },
  { key: 'work_center', label: 'Work centers' },
  { key: 'phase', label: 'Phases' },
  { key: 'shop_time_category', label: 'Time categories' },
  { key: 'time_category_list', label: 'Time lists' },
];

export function Library() {
  const { table = 'home' } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tab = params.get('view') === 'engineering' ? 'home' : table;
  useCrumb([{ label: 'Library', to: '/library' }, ...(tab !== 'home' ? [{ label: TABS.find((t) => t.key === tab)?.label ?? tab }] : [])]);
  const def = TABLES.find((t) => t.table === tab);
  // labor classes and their rates are one tab now; an old link lands there
  if (tab === 'labor_class') return <Navigate to="/library/rates" replace />;
  return (
    <div>
      <Head title="Library" sub="What the shop knows how to build, with what, in how many minutes, and which of it has been checked." />
      <div style={{ marginBottom: 12 }}>
        <Seg items={TABS} value={tab} onChange={(k) => nav(k === 'home' ? '/library' : `/library/${k}`)} />
      </div>
      {tab === 'home' && <LibraryHome />}
      {tab === 'catalog' && <CatalogList />}
      {tab === 'families' && <CatalogLists />}
      {tab === 'rates' && <Rates />}
      {def && <ListDetail key={def.table} def={def} />}
    </div>
  );
}

// ---------- verify ----------

export function VerifyButton({ table, row }: { table: string; row: Row }) {
  const { can, user } = useSession();
  if (row.verified) {
    return (
      <span className="nowrap">
        <Tag kind="on">verified</Tag> <small>{longDate(row.verified_at)}</small>
      </span>
    );
  }
  return can('verify') ? (
    <Btn small primary onClick={() => void updateRow(table, row.id, { verified: true, verified_by: user?.id ?? null })}>
      Verify
    </Btn>
  ) : (
    <Tag kind="un">unverified</Tag>
  );
}

// ---------- the home: what needs checking, and the engineering queue ----------

function LibraryHome() {
  const queue = useRows('engineering_request', { order: ['created_at'] });
  const { can } = useSession();
  const open = queue.rows.filter((q) => q.status !== 'verified');

  return (
    <div className="col">
      <CheckSheet />
      <div className="col">
        <LibrarySuggestions />
        <Panel soft={open.length > 0} label={`Engineering queue (${open.length})`} right={<Info left>Custom items marked reusable, and new catalog items, arrive here from pass 2 or the Item tab. Bennett finishes the inputs, route and BOM; Spencer verifies.</Info>}>
          {open.length === 0 && <Empty>Nothing waiting.</Empty>}
          {open.map((q) => (
            <Ln
              key={q.id}
              left={
                <>
                  {q.catalog_item_id ? <Link to={`/library/catalog/${q.catalog_item_id}`}>{q.title}</Link> : q.title}
                  <br />
                  <small>
                    {q.status} · {longDate(q.created_at)}
                  </small>
                </>
              }
              right={
                <span className="nowrap">
                  <select value={q.status} onChange={(e) => void updateRow('engineering_request', q.id, { status: e.target.value })} disabled={!can('screen.recipe_editor')}>
                    {['requested', 'drafting', 'ready', ...(can('verify') ? ['verified'] : [])].map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>{' '}
                  {!q.catalog_item_id && can('screen.recipe_editor') && (
                    <Btn
                      small
                      onClick={async () => {
                        const item = await insertRow('catalog_item', { name: q.title.replace(/\s*\(from .*\)$/, '') || `New item ${today()}` });
                        await insertRow('recipe', { catalog_item_id: item.id, name: 'Standard', is_default: true });
                        await updateRow('engineering_request', q.id, { catalog_item_id: item.id, status: 'drafting' });
                      }}
                    >
                      Start catalog item
                    </Btn>
                  )}
                </span>
              }
            />
          ))}
        </Panel>
      </div>
    </div>
  );
}

// ---------- catalog list ----------

/** Which families are open on the Catalog items list: every family starts closed (Spencer, 2026-10-06); the ones opened, and the one an item was opened from, stay open for the session. */
const OPEN_KEY = 'zg.catalog.open';
function readOpen(): Record<string, boolean> {
  try {
    return JSON.parse(sessionStorage.getItem(OPEN_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}
function writeOpen(v: Record<string, boolean>) {
  try {
    sessionStorage.setItem(OPEN_KEY, JSON.stringify(v));
  } catch {
    /* no session storage: the list simply starts closed each time */
  }
}

function CatalogList() {
  const cats = useRows('catalog_item', { order: ['name'] });
  const recipes = useRows('recipe');
  const { can, setting } = useSession();
  const nav = useNavigate();
  const [opened, setOpened] = useState<Record<string, boolean>>(readOpen);
  const setOpen = (next: Record<string, boolean>) => {
    setOpened(next);
    writeOpen(next);
  };
  const families = setting<string[]>('catalog_families', []);
  // a retired item sits in its own group at the end, closed, whatever its family
  const familyOf = (c: Row) => (c.retired ? 'Retired' : (c.family as string) || 'No family');
  const groups = [...families, ...[...new Set(cats.rows.map(familyOf))].filter((f) => !families.includes(f) && f !== 'Retired').sort(), 'Retired'].filter((f) => cats.rows.some((c) => familyOf(c) === f));
  return (
    <Panel
      label={`Catalog items (${cats.rows.length}) · by family`}
      right={
        <span className="nowrap">
          <Info left>Grouped by family (the families are their own tab). Every family starts closed; tap one to open it. The families are edited under Catalog families.</Info>{' '}
          <Btn small read onClick={() => setOpen(Object.fromEntries(groups.map((f) => [f, true])))}>
            Open all
          </Btn>{' '}
          <Btn small read onClick={() => setOpen({})}>
            Close all
          </Btn>
          {can('screen.recipe_editor') && (
            <>
              {' '}
              <Btn
                small
                onClick={async () => {
                  const item = await insertRow('catalog_item', { name: `New catalog item ${Date.now() % 10000}` });
                  await insertRow('recipe', { catalog_item_id: item.id, name: 'Standard', is_default: true });
                  nav(`/library/catalog/${item.id}`);
                }}
              >
                + Catalog item
              </Btn>
            </>
          )}
        </span>
      }
    >
      {cats.rows.length === 0 && <Empty>No catalog items yet.</Empty>}
      {groups.map((family) => {
        const here = cats.rows.filter((c) => familyOf(c) === family);
        const open = !!opened[family];
        return (
          <div key={family} style={{ marginBottom: 8 }}>
            <div className="lb" style={{ cursor: 'pointer' }} onClick={() => setOpen({ ...opened, [family]: !open })}>
              <span>
                {open ? '▾' : '▸'} {family} ({here.length})
                {family === 'Retired' && <small> · kept for old jobs, offered to no new one</small>}
              </span>
              <span>{here.filter((c) => !c.verified).length > 0 && <Tag kind="un">{here.filter((c) => !c.verified).length} unverified</Tag>}</span>
            </div>
            {open &&
              here.map((c) => (
                <Ln
                  key={c.id}
                  onClick={() => {
                    // the family an item is opened from stays open when you come back
                    setOpen({ ...opened, [family]: true });
                    nav(`/library/catalog/${c.id}`);
                  }}
                  left={
                    <>
                      {/* the name is a real link (Cmd-click to a new tab, right-click copy); the rest of the row still opens the item (design review D8) */}
                      <Link className="rowlink" to={`/library/catalog/${c.id}`} onClick={() => setOpen({ ...opened, [family]: true })}>
                        <b>{c.name}</b>
                      </Link>{' '}
                      v{c.version}
                      <br />
                      <small>
                        {c.mark_threshold === 'top_tier' ? 'top-tier (gets a mark)' : c.mark_threshold === 'no_mark' ? 'top-level, needs no mark' : 'sub-item'} ·{' '}
                        {recipes.rows
                          .filter((r) => r.catalog_item_id === c.id)
                          .map((r) => `${r.name}${r.verified ? ' ✓' : ''}`)
                          .join(' / ') || 'no recipe yet'}
                        {c.recipe_standard ? ` · picked by the job's ${String(c.recipe_standard).replace(/_/g, ' ')}` : ''}
                      </small>
                    </>
                  }
                  right={(() => {
                    // verified recipe by recipe: a job reads the recipe it uses (Spencer, 2026-10-14)
                    const rs = recipes.rows.filter((r) => r.catalog_item_id === c.id);
                    const v = rs.filter((r) => r.verified).length;
                    const left = rs.length - v;
                    if (c.verified) return <span className="nowrap"><Tag kind="on">verified</Tag>{left > 0 && <small> · {left} {left === 1 ? 'recipe' : 'recipes'} not yet</small>}</span>;
                    if (v > 0) return <Tag kind="un">item not yet · {v} of {rs.length} recipes verified</Tag>;
                    return <Tag kind="un">unverified</Tag>;
                  })()}
                />
              ))}
          </div>
        );
      })}
    </Panel>
  );
}

/** The families that group the catalog: their own tab (Spencer, 2026-10-06). */
function CatalogLists() {
  const { can, user } = useSession();
  const cats = useRows('catalog_item');
  return (
    <Panel
      label="Catalog families"
      right={
        <Info left>
          The choices in every item's Family dropdown, in the order they group the Catalog items list. Renaming a family renames it on every item in it. Removing one leaves its items as they are; they show after the listed families until they are given another.
        </Info>
      }
    >
      <SettingRows
        keys={['catalog_families']}
        bare
        disabled={!can('screen.settings') && !can('screen.library')}
        hooks={{
          catalog_families: {
            one: 'family',
            // the setting and the items in the family change in one step
            rename: async (from, to) => {
              const n = await rpc<number>('rename_catalog_family', { p_from: from, p_to: to, p_user: user?.id ?? null });
              refresh('setting', 'catalog_item');
              if (n) toast(`${n} catalog item${n === 1 ? '' : 's'} moved from "${from}" to "${to}".`);
            },
            beforeRemove: (name) => {
              const n = cats.rows.filter((c) => c.family === name).length;
              return window.confirm(
                n
                  ? `${n} catalog item${n === 1 ? ' is' : 's are'} in "${name}". ${n === 1 ? 'It keeps' : 'They keep'} the name and show${n === 1 ? 's' : ''} after the listed families until given another.\n\nRemove "${name}" from the list?`
                  : `Remove the family "${name}"? No catalog item is in it.`,
              );
            },
          },
        }}
      />
    </Panel>
  );
}

// ---------- rates and time standards, by standards version ----------

/**
 * A rate or a time standard is one row per standards version. Changing either starts a new
 * version, named by date, with a list of what changed; a job resolves at the version in
 * force until it is locked. Shared by the Rates tab and the time standard on an operation.
 */
export function useStandardsChange() {
  const versions = useRows('standards_version');
  const [busy, setBusy] = useState(false);
  const cur = currentVersion(versions.rows);
  const change = async (table: 'rate' | 'time_standard', match: Row, field: string, value: string | null, what: string) => {
    if (busy || !cur) return;
    setBusy(true);
    try {
      let versionId = cur.id as string;
      const todayName = `${today()} standards`;
      if (cur.name !== todayName) {
        versionId = await rpc<string>('new_standards_version', { p_name: todayName, p_note: `Changed from "${cur.name}".` });
      }
      const existing = await findRow(table, { ...match, standards_version_id: versionId });
      if (existing) await updateRow(table, existing.id, { [field]: value });
      else await insertRow(table, { ...match, standards_version_id: versionId, [field]: value });
      const v = await findRow('standards_version', { id: versionId });
      await updateRow('standards_version', versionId, { changes: [...((v?.changes as string[]) ?? []), what] });
      refresh('standards_version', 'rate', 'time_standard', 'operation');
    } finally {
      setBusy(false);
    }
  };
  return { cur, versions: versions.rows, busy, change };
}

/** The minutes per unit of one operation at the version in force: one time standard per operation, edited where the operation is. */
function OperationTime({ op, canEdit }: { op: Row; canEdit: boolean }) {
  const times = useRows('time_standard');
  const { cur, busy, change } = useStandardsChange();
  if (!cur) return null;
  const t = times.rows.find((x) => x.operation_id === op.id && x.standards_version_id === cur.id);
  return (
    <Panel
      label="Time standard · minutes per unit"
      right={
        <Info left>
          One time standard per operation: the minutes it takes per {op.unit || 'unit'}{op.setup_minutes ? `, plus ${qty(op.setup_minutes)} minutes of setup paid once per job${op.batch_size ? ` or per batch of ${qty(op.batch_size)}` : ''}` : ''}. Changing it starts a new standards version, named by date (the version in force is on the Rates tab); a job resolves at the version in force until it is locked. Verifying the operation covers its minutes; a change to the minutes returns the operation to unverified.
        </Info>
      }
    >
      <Ln
        left={
          <>
            Minutes per {op.unit || 'unit'} <small>· at “{cur.name}”</small>
            {!t?.minutes_per_unit && <div className="warn">No time standard yet: every line that uses this operation prices at zero and says so.</div>}
          </>
        }
        right={
          <span className="nowrap">
            <Edit
              className="cell short right"
              disabled={!canEdit || busy}
              value={t?.minutes_per_unit ?? ''}
              placeholder="none"
              parse={(x) => (x.trim() === '' ? '' : /^\d*\.?\d+$/.test(x.trim()) ? x.trim() : undefined)}
              onSave={(v) => change('time_standard', { operation_id: op.id }, 'minutes_per_unit', v === '' ? null : v, `${op.name}: ${t?.minutes_per_unit ?? 'none'} → ${v === '' ? 'none' : v} min ${op.unit ?? ''}`)}
            />
          </span>
        }
      />
    </Panel>
  );
}

/** The minutes per unit beside an operation in the list, so the list can be scanned for what is set. */
function OpMinutes({ op }: { op: Row }) {
  const versions = useRows('standards_version');
  const times = useRows('time_standard');
  const cur = currentVersion(versions.rows);
  const t = cur ? times.rows.find((x) => x.operation_id === op.id && x.standards_version_id === cur.id) : undefined;
  return <> · {t?.minutes_per_unit ? `${qty(t.minutes_per_unit)} min per ${op.unit || 'unit'}` : 'no minutes yet'}</>;
}

/**
 * Labor rates: one list, a labor class per row with its dollars per hour. A class and its rate
 * are one thing to the person using it; underneath, the rate is kept per standards version so
 * a locked job keeps the rates it was priced at.
 */
function Rates() {
  const classes = useRows('labor_class', { order: ['sort', 'name'] });
  const rates = useRows('rate');
  const ops = useRows('operation');
  const { can } = useSession();
  const { cur, versions, busy, change } = useStandardsChange();
  if (!cur) return <Loading />;
  const canEdit = useSession().canEdit('screen.library');
  const drag = useDragRows('labor_class', classes.rows, !canEdit);
  const remove = async (c: Row) => {
    const used = ops.rows.filter((o) => o.labor_class_id === c.id);
    if (used.length) {
      window.alert(`"${c.name}" cannot be removed: ${used.length} operation${used.length === 1 ? ' uses' : 's use'} it (${used.slice(0, 5).map((o) => o.name).join(', ')}${used.length > 5 ? ', …' : ''}). Give ${used.length === 1 ? 'it' : 'them'} another class first.`);
      return;
    }
    if (!window.confirm(`Remove the labor class "${c.name}" and its rate? This cannot be undone.`)) return;
    await deleteRow('labor_class', c.id);
    refresh('rate');
  };

  return (
    <div className="stack">
      <Panel label={`Standards version in force · ${cur.name}`} right={<Info left>Labor rates and time standards are one bundle. Changing either starts a new version, named by date, with a list of what changed. A job resolves at the version in force until it is locked.</Info>}>
        <small>{cur.note}</small>
        {Array.isArray(cur.changes) && cur.changes.length > 0 && (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {(cur.changes as string[]).map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        )}
        {versions.length > 1 && <small> · {versions.length} versions in all</small>}
      </Panel>
      <div className="row">
        <div className="col">
          <Panel
            label={`Labor rates · ${classes.rows.length} ${classes.rows.length === 1 ? 'class' : 'classes'}, dollars per hour`}
            right={
              <>
                <Info left>Each row is a labor class and what an hour of it costs. An operation names the class that costs it (minutes × this rate). The order here is the order everywhere a class is listed.</Info>{' '}
                {canEdit && (
                  <Btn small onClick={() => void insertRow('labor_class', { name: `New class ${classes.rows.length + 1}`, sort: nextSort(classes.rows) })}>
                    + New class
                  </Btn>
                )}
              </>
            }
          >
            {classes.rows.length === 0 && <Empty>No labor classes yet.</Empty>}
            {classes.rows.map((c) => {
              const r = rates.rows.find((x) => x.labor_class_id === c.id && x.standards_version_id === cur.id);
              const used = ops.rows.filter((o) => o.labor_class_id === c.id).length;
              return (
                <Ln
                  key={c.id}
                  {...drag.row(c.id)}
                  left={
                    <>
                      {canEdit && <Grip {...drag.grip(c.id)} />}
                      <Edit className="cell" disabled={!canEdit} value={c.name} parse={(t) => (t.trim() && !classes.rows.some((x) => x.id !== c.id && x.name.toLowerCase() === t.trim().toLowerCase()) ? t.trim() : undefined)} onSave={(v) => updateRow('labor_class', c.id, { name: v })} />
                      <br />
                      <small>{used ? `${used} operation${used === 1 ? '' : 's'}` : 'no operation uses it yet'}{!r && ' · no rate yet: its minutes price at zero'}</small>
                    </>
                  }
                  right={
                    <span className="nowrap">
                      $
                      <Edit
                        className="cell short right"
                        disabled={!canEdit || busy}
                        placeholder="per hour"
                        value={r ? Number(r.dollars_per_hour).toFixed(2) : ''}
                        parse={(t) => (/^\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined)}
                        onSave={(v) => change('rate', { labor_class_id: c.id }, 'dollars_per_hour', v, `${c.name}: $${r ? Number(r.dollars_per_hour).toFixed(2) : '—'} → $${Number(v).toFixed(2)} per hour`)}
                      />{' '}
                      {r && <VerifyButton table="rate" row={r} />}
                      {canEdit && (
                        <>
                          {' '}
                          <Btn small title="Remove this class" onClick={() => void remove(c)}>
                            ×
                          </Btn>
                        </>
                      )}
                    </span>
                  }
                />
              );
            })}
          </Panel>
        </div>
        <div className="col">
          <Panel label="Time standards">
            <p className="muted">
              Each operation carries its one time standard (minutes per unit). Set it on the operation itself, under <Link to="/library/operation">Operations</Link>; the list there shows the minutes beside every operation.
            </p>
          </Panel>
        </div>
      </div>
    </div>
  );
}

// ---------- list + detail, for every simple table ----------

export function ListDetail({ def }: { def: TableDef }) {
  const [params, setParams] = useSearchParams();
  const rows = useRows(def.table, { order: def.order });
  const refs: Record<string, Row[]> = {};
  for (const c of def.cols) {
    // the column list is a constant per table, so the hooks run in the same order every render
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // a dropdown of an ordered list follows that list's order (Spencer, 2026-10-08: note 73)
    if (c.kind === 'ref' && c.ref) refs[c.ref] = useRows(c.ref, { order: c.ref === 'work_center' ? ['name'] : ['sort', 'name'] }).rows;
  }
  const { can } = useSession();
  const [filter, setFilter] = useState('');
  const [only, setOnly] = useState(false);
  const [picks, setPicks] = useState<Record<string, string>>({});
  // how a grouped list is organised (Materials: by category or by vendor), and which groups are open; both kept for the session
  const [groupingKey, setGroupingKey] = useState<string>(() => remembered(`zg.list.${def.table}.group`) ?? def.groupings?.[0]?.key ?? '');
  const [opened, setOpened] = useState<Record<string, boolean>>(() => rememberedJson(`zg.list.${def.table}.open`));
  const names = useInventoryNames();
  const canEdit = def.library ? useSession().canEdit('screen.library') : useSession().canEdit('screen.settings');
  const look = (table: string, id: string) => refs[table]?.find((r) => r.id === id)?.name ?? '';
  const selectedId = params.get('row');
  const selected = rows.rows.find((r) => r.id === selectedId);
  const filtering = !!filter || only || Object.values(picks).some(Boolean);
  const shown = rows.rows.filter(
    (r) =>
      (!filter || `${def.title(r)} ${def.sub?.(r, look) ?? ''}`.toLowerCase().includes(filter.toLowerCase())) &&
      (!only || !r.verified) &&
      (def.filters ?? []).every((f) => {
        if (!picks[f.key]) return true;
        const v = f.of(r, look);
        return Array.isArray(v) ? v.includes(picks[f.key]!) : v === picks[f.key];
      }),
  );
  // a list kept in order is moved with arrows, never by a typed number; the arrows hide while a filter is on, so a row is never moved past ones that are hidden
  const ordered = def.order[0] === 'sort';
  const chosen = def.groupings?.find((g) => g.key === groupingKey) ?? def.groupings?.[0];
  // a grouping with no levels is the flat list in its own order
  const grouping = chosen && chosen.levels.length > 0 ? chosen : undefined;
  // a flat ordered list is reordered by dragging the grip (the Library is desk work); never while a filter hides rows
  const draggable = ordered && canEdit && !filtering && !grouping;
  const drag = useDragRows(def.table, rows.rows, !draggable);
  const pick = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('row', id);
    else next.delete('row');
    setParams(next, { replace: true });
  };
  const setOpen = (next: Record<string, boolean>) => {
    setOpened(next);
    remember(`zg.list.${def.table}.open`, JSON.stringify(next));
  };

  /** A copy of the selected row: every editable column, the title marked "(copy)", its own key, no Admin ID, unverified, at the bottom of its list. */
  const duplicate = async () => {
    if (!selected) return;
    const copy: Row = {};
    for (const c of def.cols) {
      // a column drawn from other rows (where a material is used) is not the row's own
      if (c.kind === 'readonly' || !(c.key in selected)) continue;
      copy[c.key] = selected[c.key] ?? null;
    }
    const titleKey = def.cols.find((c) => c.kind === undefined || c.kind === 'text')?.key ?? 'name';
    if (typeof copy[titleKey] === 'string') copy[titleKey] = `${copy[titleKey]} (copy)`;
    for (const k of ['admin004_id', 'admin005_id', 'part_number']) if (k in copy) copy[k] = null;
    if (typeof selected.key === 'string') copy.key = `${selected.key}_copy`;
    if (ordered) copy.sort = nextSort(rows.rows);
    const row = await insertRow(def.table, def.copy ? def.copy(copy, selected) : copy);
    pick(row.id);
  };

  const line = (r: Row) => (
    <Ln
      key={r.id}
      sel={r.id === selectedId}
      onClick={() => pick(r.id)}
      {...(draggable ? drag.row(r.id) : {})}
      className={draggable ? drag.row(r.id).className : undefined}
      left={
        <>
          {draggable && (
            <>
              <Grip {...drag.grip(r.id)} />{' '}
            </>
          )}
          {def.title(r)}
          {def.sub && (
            <>
              {grouping ? ' ' : <br />}
              <small>
                {def.sub(r, look)}
                {def.table === 'operation' && <OpMinutes op={r} />}
              </small>
            </>
          )}
        </>
      }
      right={<span className="nowrap">{def.library && !r.verified && <Tag kind="un">unverified</Tag>}</span>}
    />
  );

  /** Two levels of groups (category, then subcategory; or vendor, then category), each closed until opened; a filter opens every group with a hit. */
  const grouped = () => {
    if (!grouping) return null;
    const byTitle = (a: Row, b: Row) => def.title(a).localeCompare(def.title(b));
    const top = new Map<string, Map<string, Row[]>>();
    for (const r of shown) {
      const a = grouping.levels[0]!(r, look) || '(none)';
      const b = grouping.levels[1] ? grouping.levels[1](r, look) || '(none)' : '';
      if (!top.has(a)) top.set(a, new Map());
      const inner = top.get(a)!;
      if (!inner.has(b)) inner.set(b, []);
      inner.get(b)!.push(r);
    }
    const tops = [...top.keys()].sort((a, b) => a.localeCompare(b));
    return tops.map((a) => {
      const inner = top.get(a)!;
      const count = [...inner.values()].reduce((n, x) => n + x.length, 0);
      const unverified = [...inner.values()].flat().filter((r) => !r.verified).length;
      const openA = filtering || !!opened[a];
      return (
        <div key={a} style={{ marginBottom: 6 }}>
          <div className="lb" style={{ cursor: 'pointer' }} onClick={() => setOpen({ ...opened, [a]: !openA })}>
            <span>
              {openA ? '▾' : '▸'} {a} ({count})
            </span>
            <span>{def.library && unverified > 0 && <Tag kind="un">{unverified} unverified</Tag>}</span>
          </div>
          {openA &&
            [...inner.keys()]
              .sort((x, y) => x.localeCompare(y))
              .map((b) => {
                const list = inner.get(b)!.slice().sort(byTitle);
                if (!b) return list.map(line);
                const k = `${a} · ${b}`;
                const openB = filtering || !!opened[k];
                return (
                  <div key={k} style={{ marginLeft: 12 }}>
                    <div className="lb" style={{ cursor: 'pointer' }} onClick={() => setOpen({ ...opened, [k]: !openB })}>
                      <span>
                        {openB ? '▾' : '▸'} {b} ({list.length})
                      </span>
                    </div>
                    {openB && list.map(line)}
                  </div>
                );
              })}
        </div>
      );
    });
  };

  return (
    <div className={`listdetail${def.wide ? ' wide' : ''}`}>
      <Panel
        label={`${def.label} (${shown.length}${shown.length !== rows.rows.length ? ` of ${rows.rows.length}` : ''})`}
        right={
          <span className="nowrap">
            {def.groupings && def.groupings.length > 1 && (
              <>
                <Seg
                  items={def.groupings.map((g) => ({ key: g.key, label: g.label }))}
                  value={chosen?.key ?? ''}
                  onChange={(k) => {
                    setGroupingKey(k);
                    remember(`zg.list.${def.table}.group`, k);
                    setOpen({});
                  }}
                />{' '}
              </>
            )}
            {grouping && (
              <>
                <Btn small read onClick={() => setOpen({})}>
                  Close all
                </Btn>{' '}
              </>
            )}
            {canEdit && (
              <Btn
                small
                onClick={async () => {
                  // an ordered list's new row lands at the bottom
                  const row = await insertRow(def.table, { ...def.blank, ...(ordered ? { sort: nextSort(rows.rows) } : {}) });
                  pick(row.id);
                }}
              >
                + New
              </Btn>
            )}
          </span>
        }
      >
        <div className="row mid" style={{ marginBottom: 6, gap: 6, flexWrap: 'wrap' }}>
          <input type="search" className="grow" placeholder={`Filter ${def.label.toLowerCase()}`} value={filter} onChange={(e) => setFilter(e.target.value)} />
          {(def.filters ?? []).map((f) => {
            const values = [...new Set(rows.rows.flatMap((r) => f.of(r, look)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
            return (
              <select key={f.key} className="read" value={picks[f.key] ?? ''} onChange={(e) => setPicks({ ...picks, [f.key]: e.target.value })} title={`Only one ${f.label.toLowerCase()}`}>
                <option value="">{f.label}: all</option>
                {values.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            );
          })}
          {def.library && (
            <label className="check">
              <input type="checkbox" className="read" checked={only} onChange={(e) => setOnly(e.target.checked)} /> unverified
            </label>
          )}
        </div>
        <div className="list">
          {rows.loading && <Loading />}
          {!rows.loading && shown.length === 0 && <Empty>Nothing here yet.</Empty>}
          {grouping ? grouped() : shown.filter((r) => r.retired !== true && !r.retired_at).map(line)}
          {!grouping && shown.some((r) => r.retired === true || !!r.retired_at) && (
            // a retired row (an operation Spencer took out of use) sits in its own fold at the end, closed, like a retired catalog item: kept for old jobs, offered to no new route
            <div style={{ marginTop: 8 }}>
              <div className="lb" style={{ cursor: 'pointer' }} onClick={() => setOpen({ ...opened, Retired: !opened.Retired })}>
                <span>
                  {opened.Retired ? '▾' : '▸'} Retired ({shown.filter((r) => r.retired === true || !!r.retired_at).length})<small> · kept for old jobs, offered to no new one</small>
                </span>
              </div>
              {opened.Retired && shown.filter((r) => r.retired === true || !!r.retired_at).map(line)}
            </div>
          )}
        </div>
        {ordered && canEdit && filtering && !grouping && <small>Clear the filter to change the order.</small>}
        {draggable && rows.rows.length > 1 && <small>Drag the grip (⋮⋮) to reorder.</small>}
      </Panel>
      {selected ? (
        <Panel label={def.title(selected)} right={def.library ? <VerifyButton table={def.table} row={selected} /> : undefined}>
          <div className="form">
            {def.cols.filter((c) => !c.show || c.show(selected) || (selected[c.key] !== null && selected[c.key] !== undefined && String(selected[c.key]).trim() !== '')).map((c) => (
              <Field key={c.key} wide={c.kind === 'long' || c.kind === 'json' || c.kind === 'custom'} label={<>{c.named ? cap(names[c.named]) : c.label}{c.info && <Info>{c.info}</Info>}</>}>
                <Cell def={def} col={c} row={selected} refs={refs} disabled={!canEdit} />
              </Field>
            ))}
          </div>
          {def.table === 'job_type' && <JobTypePulls jobTypeId={selected.id} canEdit={canEdit} />}
          {def.table === 'job_type' && <JobTypeRoute jobTypeId={selected.id} canEdit={canEdit} />}
          {def.table === 'time_category_list' && <ListAssignments listId={selected.id} canEdit={canEdit} />}
          {def.table === 'operation' && (
            <>
              <div style={{ marginTop: 14 }}>
                <OperationTime op={selected} canEdit={canEdit} />
              </div>
              <SopList operationId={selected.id} canEdit={canEdit} />
              <ChecklistLines operationId={selected.id} canEdit={canEdit} />
            </>
          )}
          {def.table === 'vendor' && <VendorExtras vendor={selected} vendors={rows.rows} canEdit={canEdit} onMerged={() => pick(null)} />}
          {def.library && selected.verified && <p className="muted" style={{ marginTop: 10 }}>Editing a verified row returns it to unverified.</p>}
          {canEdit && (
            <div className="row mid" style={{ marginTop: 12, gap: 6 }}>
              <Btn small onClick={() => void duplicate()} title={`A copy of this ${def.one} with everything on it, marked "(copy)". It lands unverified.`}>
                Duplicate
              </Btn>
              <Btn
                small
                onClick={async () => {
                  if (!window.confirm(`Delete this ${def.one}? This cannot be undone.`)) return;
                  pick(null);
                  await deleteRow(def.table, selected.id).catch(() => undefined);
                }}
              >
                Delete
              </Btn>
            </div>
          )}
        </Panel>
      ) : (
        <Panel>
          <Empty>Pick a row on the left to see and edit it.</Empty>
        </Panel>
      )}
    </div>
  );
}

/** A value kept for the session (which grouping, which groups are open). Nothing here is data: a lost value only closes the groups again. */
function remembered(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function rememberedJson(key: string): Record<string, boolean> {
  try {
    return JSON.parse(remembered(key) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}
function remember(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* no session storage */
  }
}

function Cell({ def, col, row, refs, disabled }: { def: TableDef; col: Col; row: Row; refs: Record<string, Row[]>; disabled: boolean }) {
  const save = (v: unknown) => updateRow(def.table, row.id, { [col.key]: v });
  const value = row[col.key];
  switch (col.kind) {
    case 'custom':
      return <>{col.render?.(row, disabled)}</>;
    case 'readonly':
      return <span className="lk">{value === null || value === undefined || value === '' ? '—' : String(value)}</span>;
    case 'bool':
      return <input type="checkbox" disabled={disabled} checked={!!value} onChange={(e) => void save(e.target.checked)} style={{ width: 20, height: 20 }} />;
    case 'select':
      return (
        <select disabled={disabled} value={value ?? ''} onChange={(e) => void save(e.target.value === '' && col.options!.includes('') ? null : e.target.value)}>
          {col.options!.map((o) => (
            <option key={o} value={o}>
              {o === '' ? 'not set' : o.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      );
    case 'ref':
      return (
        <select disabled={disabled} value={value ?? ''} onChange={(e) => void save(e.target.value || null)}>
          <option value="">—</option>
          {(refs[col.ref!] ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      );
    case 'number':
      return <Edit disabled={disabled} value={value === null || value === undefined ? '' : qty(value).replace('—', '')} parse={(t) => (t.trim() === '' ? '' : /^-?\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined)} onSave={(v) => save(v === '' ? null : Number(v))} />;
    case 'date':
      return <Edit type="date" disabled={disabled} value={value ?? ''} onSave={(v) => save(v || null)} />;
    case 'json':
      return (
        <Edit
          disabled={disabled}
          value={value === null || value === undefined ? '' : JSON.stringify(value)}
          parse={(t) => {
            if (t.trim() === '') return '';
            try {
              JSON.parse(t);
              return t;
            } catch {
              return undefined;
            }
          }}
          onSave={(v) => save(v === '' ? null : JSON.parse(v))}
        />
      );
    default:
      return <Edit disabled={disabled} value={value ?? ''} onSave={(v) => save(v === '' ? null : v)} />;
  }
}

/** What a vendor supplies, the names it has gone by, and folding a duplicate into another vendor. */
function VendorExtras({ vendor, vendors, canEdit, onMerged }: { vendor: Row; vendors: Row[]; canEdit: boolean; onMerged: () => void }) {
  const materials = useRows('material');
  const [into, setInto] = useState('');
  const primary = materials.rows.filter((m) => m.vendor_id === vendor.id).length;
  const alternate = materials.rows.filter((m) => m.alt_vendor_id === vendor.id).length;
  const aliases = (vendor.aliases as string[]) ?? [];
  const target = vendors.find((v) => v.id === into);
  return (
    <div style={{ marginTop: 14 }}>
      <small>
        Primary vendor for {primary} {primary === 1 ? 'material' : 'materials'}
        {alternate > 0 ? `, alternate for ${alternate}` : ''}.{aliases.length > 0 && ` Also known as: ${aliases.join(', ')}.`}
      </small>
      {canEdit && (
        <div className="row mid" style={{ marginTop: 10, gap: 6 }}>
          <span>Same vendor under another name?</span>
          <select value={into} onChange={(e) => setInto(e.target.value)}>
            <option value="">Merge into…</option>
            {vendors.filter((v) => v.id !== vendor.id).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
          <Btn
            small
            disabled={!target}
            onClick={async () => {
              if (!target) return;
              if (!window.confirm(`Merge "${vendor.name}" into "${target.name}"? Its ${primary + alternate} materials move to ${target.name}, and "${vendor.name}" is kept only as another name for it. This cannot be undone.`)) return;
              onMerged();
              await rpc('merge_vendor', { p_from: vendor.id, p_into: target.id });
              refresh('vendor', 'material');
            }}
          >
            Merge
          </Btn>
        </div>
      )}
    </div>
  );
}

/** Who logs time against a list: roles and people. A list given to a person by name wins over the lists of their role. */
function ListAssignments({ listId, canEdit }: { listId: string; canEdit: boolean }) {
  const rows = useRows('time_category_assignment', { eq: { list_id: listId } });
  const users = useRows('user', { order: ['name'] });
  const cats = useRows('shop_time_category', { eq: { list_id: listId }, order: ['sort'] });
  const roles = ['owner', 'office', 'shop', 'contractor'];
  const toggle = (match: Row, on: boolean) => {
    const have = rows.rows.find((x) => (match.role ? x.role === match.role : x.user_id === match.user_id));
    return on ? insertRow('time_category_assignment', { list_id: listId, ...match }) : have ? deleteRow('time_category_assignment', have.id) : Promise.resolve();
  };
  return (
    <div className="row" style={{ marginTop: 14 }}>
      <div className="col">
        <div className="lb">
          <span>Roles that log time against this list</span>
        </div>
        {roles.map((role) => (
          <label key={role} className="check" style={{ display: 'flex', padding: '3px 0' }}>
            <input type="checkbox" disabled={!canEdit} checked={rows.rows.some((x) => x.role === role)} onChange={(e) => void toggle({ role }, e.target.checked)} />
            {role}
          </label>
        ))}
        <div className="lb" style={{ marginTop: 10 }}>
          <span>People given this list by name</span>
        </div>
        {users.rows.map((u) => (
          <label key={u.id} className="check" style={{ display: 'flex', padding: '3px 0' }}>
            <input type="checkbox" disabled={!canEdit} checked={rows.rows.some((x) => x.user_id === u.id)} onChange={(e) => void toggle({ user_id: u.id }, e.target.checked)} />
            {u.name}
          </label>
        ))}
        <small>A list given to a person by name wins over the lists of their role.</small>
      </div>
      <div className="col">
        <div className="lb">
          <span>Categories in this list · {cats.rows.length}</span>
        </div>
        {cats.rows.length === 0 && <Empty>None yet. Add them under Time categories and pick this list.</Empty>}
        {cats.rows.map((c) => (
          <div key={c.id} style={{ padding: '3px 0' }}>
            {c.name}
          </div>
        ))}
      </div>
    </div>
  );
}

/** What a job type pulls in: which standards a job of this type sees, and which catalog items it offers. */
function JobTypePulls({ jobTypeId, canEdit }: { jobTypeId: string; canEdit: boolean }) {
  const defs = useRows('standard_def', { order: ['sort'] });
  const pulls = useRows('job_type_standard', { eq: { job_type_id: jobTypeId }, order: ['sort', 'created_at'] });
  const cats = useRows('catalog_item', { order: ['name'] });
  const offers = useRows('job_type_catalog_item', { eq: { job_type_id: jobTypeId } });
  // the pulled ones first, in the order Job setup lists them (drag to change it); the rest below, to tick in
  const drag = useDragRows('job_type_standard', pulls.rows, !canEdit);
  const pulled = pulls.rows.map((p) => ({ p, d: defs.rows.find((d) => d.id === p.standard_def_id) })).filter((x) => x.d && x.d.kind !== 'catalog');
  const rest = defs.rows.filter((d) => d.kind !== 'catalog' && !pulls.rows.some((p) => p.standard_def_id === d.id));
  return (
    <div className="row" style={{ marginTop: 14 }}>
      <div className="col">
        <div className="lb">
          <span>Standards this type pulls in · {pulled.length}</span>
          <Info left>A job of this type sees these on Job setup, in this order: drag the grip to put what a job decides first. Untick one and jobs of this type stop showing it (its company default still applies).</Info>
        </div>
        {pulled.map(({ p, d }) => (
          <label key={p.id} {...drag.row(p.id, 'check dragrow')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
            {canEdit && <Grip {...drag.grip(p.id)} />}
            <input type="checkbox" disabled={!canEdit} checked onChange={() => void deleteRow('job_type_standard', p.id)} />
            {d!.label} <small className="muted">{d!.kind}</small>
          </label>
        ))}
        {rest.length > 0 && (
          <div className="lb" style={{ marginTop: 8 }}>
            <span>Not pulled in</span>
          </div>
        )}
        {rest.map((d) => (
          <label key={d.id} className="check" style={{ display: 'flex', padding: '3px 0' }}>
            <input type="checkbox" disabled={!canEdit} checked={false} onChange={() => void insertRow('job_type_standard', { job_type_id: jobTypeId, standard_def_id: d.id, sort: nextSort(pulls.rows) })} />
            {d.label} <small className="muted">{d.kind}</small>
          </label>
        ))}
      </div>
      <div className="col">
        <div className="lb">
          <span>Catalog items it offers · {offers.rows.length}</span>
          <Info left>
            A ticked item is in + Item on a job of this type. <b>Every job starts with it</b> adds one of the item to each new job of this type as it is created, numbered the way + Item numbers it (the care kit lands as K-01); it can be removed from a job like any other item. Jobs already made are not changed.
          </Info>
        </div>
        {cats.rows.map((c) => {
          const o = offers.rows.find((x) => x.catalog_item_id === c.id);
          return (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0', flexWrap: 'wrap' }}>
              <label className="check" style={{ display: 'flex', flex: '1 1 160px', margin: 0 }}>
                <input type="checkbox" disabled={!canEdit} checked={!!o} onChange={(e) => void (e.target.checked ? insertRow('job_type_catalog_item', { job_type_id: jobTypeId, catalog_item_id: c.id, starts_on_job: false }) : deleteRow('job_type_catalog_item', o!.id))} />
                {c.name}
              </label>
              {o && (
                <label className="check" style={{ display: 'flex', margin: 0 }} title="Every new job of this type starts with one of this item">
                  <input type="checkbox" disabled={!canEdit} checked={o.starts_on_job === true} onChange={(e) => void updateRow('job_type_catalog_item', o.id, { starts_on_job: e.target.checked })} />
                  <small className="muted">every job starts with it</small>
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Category on an operation: one of the time categories, every list (Spencer, 2026-10-08: note 78). An old word that is not one stays until picked again, and says so. */
function CategoryPick({ row, disabled }: { row: Row; disabled: boolean }) {
  const cats = useRows('shop_time_category', { order: ['sort', 'name'] });
  const lists = useRows('time_category_list', { order: ['sort', 'name'] });
  const cur = (row.category as string | null) ?? '';
  const stray = cur !== '' && !cats.rows.some((c) => c.name === cur);
  return (
    <select disabled={disabled} value={cur} onChange={(e) => void updateRow('operation', row.id, { category: e.target.value || null })}>
      <option value="">—</option>
      {stray && <option value={cur}>{cur} (not a time category)</option>}
      {lists.rows.map((l) => (
        <optgroup key={l.id} label={l.name}>
          {cats.rows
            .filter((c) => c.list_id === l.id)
            .map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * The job route (Spencer, 2026-10-08): the job-level steps of a job type (site measurements, design, the install extras) and its
 * job-level BOM lines (the misc hardware allowance). A job of the type gets every step as a line to type a quantity against, in the
 * operation's unit, and every BOM line with the quantity it starts with. A step added here reaches every open job of the type.
 */
function JobTypeRoute({ jobTypeId, canEdit }: { jobTypeId: string; canEdit: boolean }) {
  const steps = useRows('job_type_route_step', { eq: { job_type_id: jobTypeId }, order: ['sort', 'created_at'] });
  const lines = useRows('job_type_bom_line', { eq: { job_type_id: jobTypeId }, order: ['sort', 'created_at'] });
  const ops = useRows('operation', { order: ['sort', 'name'] });
  const phases = useRows('phase', { order: ['sort', 'name'] });
  const mats = useRows('material', { order: ['description'] });
  const dragSteps = useDragRows('job_type_route_step', steps.rows, !canEdit);
  const dragLines = useDragRows('job_type_bom_line', lines.rows, !canEdit);
  const live = ops.rows.filter((o) => !o.retired);
  const groups = [
    ...phases.rows.map((p) => ({ key: p.id as string, label: p.name as string, ops: live.filter((o) => o.phase_id === p.id) })),
    { key: 'none', label: 'No phase', ops: live.filter((o) => !o.phase_id || !phases.rows.some((p) => p.id === o.phase_id)) },
  ].filter((g) => g.ops.length > 0);
  const unit = (o: Row | undefined) => String(o?.unit || 'unit').replace(/^\//, '');
  const free = live.filter((o) => !steps.rows.some((s) => s.operation_id === o.id));
  // a note is never narrower than its text: a long one takes the next line of its row to itself
  const noteBasis = (t: unknown) => Math.min(560, Math.max(220, String(t ?? '').length * 7 + 28));
  const number = (t: string) => (t.trim() === '' ? '' : /^\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined);
  return (
    <div className="stack" style={{ marginTop: 14 }}>
      <Panel
        label={`Job route · ${steps.rows.length} ${steps.rows.length === 1 ? 'step' : 'steps'}`}
        right={
          <>
            <Info left>
              The job-level steps of this type: site measurements, design, proposal writing, the install extras. Each is an operation; a job of this type gets every one as a line on Job setup and types how many (minutes on site, trips, pieces) in the
              operation's unit. Give a step the quantity a job starts with (1 for design, which every job has) and each new job prices it without anyone typing; the job can still type over it. A blank prices at zero and says so. A step added here reaches every open job of the type; a job can add a step of its own there.
            </Info>{' '}
            {canEdit && free[0] && (
              <Btn small onClick={() => void insertRow('job_type_route_step', { job_type_id: jobTypeId, operation_id: free[0]!.id, sort: nextSort(steps.rows) })}>
                + Step
              </Btn>
            )}
          </>
        }
      >
        {steps.rows.length === 0 && <Empty>No job-level steps yet. Add site measurements, design, engineering and the install extras: a job of this type types how many of each.</Empty>}
        {steps.rows.map((s) => {
          const op = ops.rows.find((o) => o.id === s.operation_id);
          const phase = phases.rows.find((p) => p.id === op?.phase_id);
          return (
            <div key={s.id} style={{ display: 'block' }} {...dragSteps.row(s.id, 'ln dragrow')}>
              <div className="row mid" style={{ gap: 6 }}>
                <Grip {...dragSteps.grip(s.id)} />
                <select disabled={!canEdit} value={s.operation_id} onChange={(e) => void updateRow('job_type_route_step', s.id, { operation_id: e.target.value })} style={{ width: 260 }}>
                  {groups.map((g) => (
                    <optgroup key={g.key} label={g.label}>
                      {g.ops.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  {op && op.retired && <option value={op.id}>{op.name} (retired)</option>}
                </select>
                {phase && <small className="muted nowrap">{phase.name}</small>}
                <small className="nowrap">qty a job starts with</small>
                <Edit className="cell short right" disabled={!canEdit} value={s.qty === null || s.qty === undefined ? '' : String(s.qty)} placeholder="blank" parse={number} onSave={(v) => updateRow('job_type_route_step', s.id, { qty: v === '' ? null : v })} />
                <small className="muted nowrap">{op?.unit ? plural(op.unit) : 'no unit set'}</small>
                <span style={{ flex: `1 1 ${noteBasis(s.note)}px` }}>
                  <Edit className="cell" disabled={!canEdit} value={s.note ?? ''} placeholder="A note the job sees: minutes on site, trips each way" onSave={(v) => updateRow('job_type_route_step', s.id, { note: v || null })} />
                </span>
                {canEdit && (
                  <Btn small onClick={() => window.confirm(`Remove "${op?.name ?? 'this step'}" from the job route? A job that typed a quantity against it keeps its line.`) && void deleteRow('job_type_route_step', s.id)}>
                    ×
                  </Btn>
                )}
              </div>
            </div>
          );
        })}
      </Panel>
      <Panel
        label={`Job BOM · ${lines.rows.length} ${lines.rows.length === 1 ? 'line' : 'lines'}`}
        right={
          <>
            <Info left>Job-level materials: a per-job allowance such as misc hardware, with the quantity a job starts with (blank: the job types it). Priced like any material line.</Info>{' '}
            {canEdit && mats.rows[0] && (
              <Btn small onClick={() => void insertRow('job_type_bom_line', { job_type_id: jobTypeId, material_id: mats.rows[0]!.id, sort: nextSort(lines.rows) })}>
                + Line
              </Btn>
            )}
          </>
        }
      >
        {lines.rows.length === 0 && <Empty>No job-level materials yet.</Empty>}
        {lines.rows.map((l) => {
          const m = mats.rows.find((x) => x.id === l.material_id);
          return (
            <div key={l.id} style={{ display: 'block' }} {...dragLines.row(l.id, 'ln dragrow')}>
              <div className="row mid" style={{ gap: 6 }}>
                <Grip {...dragLines.grip(l.id)} />
                <select disabled={!canEdit} value={l.material_id} onChange={(e) => void updateRow('job_type_bom_line', l.id, { material_id: e.target.value })} style={{ width: 260 }}>
                  {mats.rows.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.description}
                    </option>
                  ))}
                </select>
                <small className="nowrap">qty a job starts with</small>
                <Edit className="cell short right" disabled={!canEdit} value={l.qty === null || l.qty === undefined ? '' : String(l.qty)} placeholder="blank" parse={number} onSave={(v) => updateRow('job_type_bom_line', l.id, { qty: v === '' ? null : v })} />
                <span style={{ flex: `1 1 ${noteBasis(l.note)}px` }}>
                  <Edit className="cell" disabled={!canEdit} value={l.note ?? ''} placeholder="Where it applies" onSave={(v) => updateRow('job_type_bom_line', l.id, { note: v || null })} />
                </span>
                <small className="muted nowrap">{m ? priceWords(m) : ''}</small>
                {canEdit && (
                  <Btn small onClick={() => window.confirm(`Remove "${m?.description ?? 'this line'}" from the job BOM?`) && void deleteRow('job_type_bom_line', l.id)}>
                    ×
                  </Btn>
                )}
              </div>
            </div>
          );
        })}
      </Panel>
    </div>
  );
}
